/**
 * "Where the $32,006 comes from" — the cashflow pop-over the owner agreed to (UX review, 17 Oct 2026).
 *
 * Money in, then money out, over a full year with the plan in place, each line with one sub-line
 * saying where it comes from; the tax line opens a second page, "How the tax is worked out", with a
 * column per person and a way back. Every figure is the strategy's own snapshot — the same seven the
 * engine returns and the Annual Snapshot tiles show — relabelled, never recomputed.
 */
import React, { useState } from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { Button } from '../../components/ui/index.tsx';
import DialogShell, { DialogSection } from '../../components/ui/DialogShell.tsx';

export interface CashflowSnapshotLike {
    pension?: number;
    abpIncome?: number;
    rentalIncome?: number;
    otherInvestmentIncome?: number;
    rentalExpenses?: number;
    taxPayable?: number;
    totalAgedCareCosts?: number;
    livingExpenses?: number;
    netCashflow?: number;
    taxBreakdown?: { label: string; value: string | number }[];
    [key: string]: unknown;
}

export interface CashflowBreakdownModalProps {
    /** The plan, in words — "Pay $320,000 up front, keep the home". */
    planTitle: string;
    snapshot: CashflowSnapshotLike;
    /** Doing nothing, for the closing line. */
    baseline: { pension: number; netCashflow: number } | null;
    /** The deposit paid and the room left unpaid, for the pension and care sub-lines. */
    deposit: { paid: number; unpaid: number; dapAnnual: number };
    /** "$150,000 from the ANZ account and $170,000 from the MLC pension" — where the deposit came from. */
    fundingLines: { label: string; amount: number }[];
    /** The account-based pensions on file, by name. */
    abpNames: string[];
    enteringName: string;
    /** The partner at home, where there is one. */
    stayingName: string | null;
    /** The two people, for the tax page's columns. */
    people: { firstName: string; pronounHer?: boolean }[];
    /** Which page to open on: the cashflow, or straight to one figure's why (owner, 18 Oct 2026). */
    initialPage?: FigurePage;
    /** The Age Pension page: the plain-words why and the engine's own calculation rows. */
    pensionWhy: React.ReactNode;
    pensionRows: { label: string; value: string | number }[];
    /** The care-costs page, likewise. */
    careWhy: React.ReactNode;
    careRows: { label: string; value: string | number }[];
    onDeepDive?: () => void;
    onClose: () => void;
}

export type FigurePage = 'cashflow' | 'tax' | 'pension' | 'care';

const money = (n: number) => formatCurrency(Math.round(Math.abs(n)));
const signed = (n: number) => (n < 0 ? `−${money(n)}` : `+${money(n)}`);

const Row = ({ label, sub, value, bold = false, why, onWhy }: { label: React.ReactNode; sub?: React.ReactNode; value: React.ReactNode; bold?: boolean; why?: boolean; onWhy?: () => void }) => (
    <div className={`flex items-start justify-between gap-6 ${bold ? 'mt-1 border-t-2 border-brand-navy pt-3.5 pb-1' : 'border-b border-slate-100 py-3'}`}>
        <div className="min-w-0">
            <p className={`${bold ? 'text-[16px] font-bold text-brand-navy' : 'text-[15px] text-slate-800'}`}>{label}</p>
            {sub && (
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-500">
                    {sub}
                    {why && onWhy && <> <button type="button" onClick={onWhy} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy">why?</button></>}
                </p>
            )}
        </div>
        <p className={`shrink-0 tabular-nums ${bold ? 'text-[18px] font-bold text-brand-navy' : 'text-[15px] font-semibold text-slate-900'}`}>{value}</p>
    </div>
);

const Section = ({ children }: { children: React.ReactNode }) => <DialogSection className="mt-6 first:mt-0">{children}</DialogSection>;

/** One person's tax lines from the engine's breakdown, relabelled for a reader; every line kept for the adviser view. */
interface TaxLine { label: string; sub?: string; value: string; indent?: boolean; adviserOnly?: boolean; bold?: boolean }
function taxLinesFor(rows: { label: string; value: string | number }[], showAll: boolean): TaxLine[] {
    const out: TaxLine[] = [];
    const val = (v: string | number) => String(v ?? '');
    const isZero = (v: string | number) => /^\$?0(\.00)?$/.test(val(v).replace(/[−-]/g, '').trim());
    for (const r of rows) {
        const l = String(r.label || '').trim();
        if (/^- /.test(l)) { out.push({ label: l.replace(/^- /, ''), value: val(r.value), indent: true }); continue; }
        if (/^salary/i.test(l)) { out.push({ label: 'Salary', value: val(r.value), adviserOnly: isZero(r.value) }); continue; }
        if (/assessable investment income/i.test(l)) { out.push({ label: 'Income that is taxed', sub: 'Interest, dividends and rent, after the account-based pensions, which are tax-free at this age', value: val(r.value) }); continue; }
        if (/^less: deductions/i.test(l)) { out.push({ label: 'Deductions', value: val(r.value), adviserOnly: isZero(r.value) }); continue; }
        if (/total taxable income/i.test(l)) { out.push({ label: 'Taxable income', value: val(r.value), adviserOnly: true }); continue; }
        if (/marginal tax rate/i.test(l)) { out.push({ label: 'Marginal tax rate', value: val(r.value), adviserOnly: true }); continue; }
        if (/gross tax payable/i.test(l)) { out.push({ label: 'Tax on that', sub: isZero(r.value) ? 'Under the tax-free threshold once the seniors offset applies, so nothing' : undefined, value: val(r.value) }); continue; }
        if (/medicare levy/i.test(l)) { out.push({ label: 'Medicare levy', value: val(r.value) }); continue; }
        if (/franking credits/i.test(l)) { out.push({ label: 'Franking credits', sub: 'Tax the companies already paid on the dividends — it comes back', value: val(r.value).replace(/^-/, '+') }); continue; }
        if (/net tax payable/i.test(l)) { const neg = /^-/.test(val(r.value)); out.push({ label: neg ? 'Refund' : 'Tax to pay', value: val(r.value).replace(/^-/, ''), bold: true }); continue; }
        out.push({ label: l, value: val(r.value), adviserOnly: true });
    }
    return out.filter((t) => showAll || !t.adviserOnly);
}

/** Split the engine's one breakdown list into a column per person ("JOHN'S TAX" headings). */
function taxColumns(rows: { label: string; value: string | number }[] | undefined): { name: string; rows: { label: string; value: string | number }[] }[] {
    const cols: { name: string; rows: { label: string; value: string | number }[] }[] = [];
    for (const r of rows || []) {
        const l = String(r.label || '').trim();
        // The engine marks each person with a heading row — "--- John's Tax ---" or "JOHN'S TAX" — and no value.
        const m = l.match(/^-*\s*(.+?)['’]s tax\s*-*$/i);
        if (m && String(r.value ?? '') === '') { cols.push({ name: m[1].trim(), rows: [] }); continue; }
        if (/^-{2,}/.test(l) && String(r.value ?? '') === '') { cols.push({ name: l.replace(/-/g, '').trim(), rows: [] }); continue; }
        if (!cols.length) cols.push({ name: '', rows: [] });
        cols[cols.length - 1].rows.push(r);
    }
    return cols;
}

const CashflowBreakdownModal = ({ planTitle, snapshot, baseline, deposit, fundingLines, abpNames, enteringName, stayingName, people, initialPage = 'cashflow', pensionWhy, pensionRows, careWhy, careRows, onDeepDive, onClose }: CashflowBreakdownModalProps) => {
    /*
     * ONE pop-over, several pages (owner, 18 Oct 2026: "I prefer where it opens up the pop-up right
     * there for you"). A "why?" on the card opens the figure's own page; a "why?" inside the
     * cashflow goes to that page with a way back; nothing sends the reader down to "Show the detail".
     */
    const [page, setPageOnly] = useState<FigurePage>(initialPage);
    // Where the reader came from, so Back always returns one step, whatever the route taken.
    const [trail, setTrail] = useState<FigurePage[]>([]);
    const setPage = (next: FigurePage) => { setTrail((t) => [...t, page]); setPageOnly(next); };
    const goBack = () => { const prev = trail[trail.length - 1]; if (prev == null) return; setTrail((t) => t.slice(0, -1)); setPageOnly(prev); };
    const canGoBack = trail.length > 0;
    const taxPage = page === 'tax';
    const setTaxPage = (on: boolean) => (on ? setPage('tax') : goBack());
    const pageName = (p: FigurePage) => p === 'pension' ? `${enteringName}'s Age Pension` : p === 'care' ? 'the care costs' : p === 'tax' ? 'the tax' : `where the ${money(net)} comes from`;
    const [adviserView, setAdviserView] = useState(false);
    const pension = Number(snapshot.pension) || 0;
    const abp = Number(snapshot.abpIncome) || 0;
    const other = (Number(snapshot.otherInvestmentIncome) || 0) + (Number(snapshot.rentalIncome) || 0);
    const rentalExpenses = Number(snapshot.rentalExpenses) || 0;
    const tax = Number(snapshot.taxPayable) || 0;
    const care = Number(snapshot.totalAgedCareCosts) || 0;
    const living = Number(snapshot.livingExpenses) || 0;
    const net = Number(snapshot.netCashflow) || 0;
    const short = net < 0;
    const headline = short ? `Each year, short by ${money(net)}` : `Left over each year: ${money(net)}`;
    const abpFunding = fundingLines.filter((l) => abpNames.some((n) => n && l.label.toLowerCase().includes(n.toLowerCase())));
    const abpToDeposit = abpFunding.reduce((s, l) => s + l.amount, 0);
    const pensionDelta = baseline ? pension - baseline.pension : null;
    const closes = baseline && baseline.netCashflow < 0 && short ? Math.max(0, Math.abs(baseline.netCashflow) - Math.abs(net)) : null;
    const cols = taxColumns(snapshot.taxBreakdown);

    const title = page === 'tax' ? 'How the tax is worked out'
        : page === 'pension' ? `Why ${enteringName}'s Age Pension is ${money(pension)}`
        : page === 'care' ? `Why care costs ${money(care)} a year`
        : short ? `Where the ${money(net)} comes from` : `Where the ${money(net)} left over comes from`;
    const intro = page === 'tax'
        ? <>{people.length > 1 ? `Each of ${people.map((p) => p.firstName).join(' and ')} is taxed on their own share. ` : ''}At these income levels the seniors tax offset usually brings tax to nothing, and a refund means franking credits on the shares came back.</>
        : page === 'pension' ? pensionWhy
        : page === 'care' ? careWhy
        : <>Money in, less money out, over a full year with this plan in place. {short ? 'When it is short, the gap comes out of savings.' : 'What is left over stays in the bank.'}</>;
    return (
        <DialogShell
            titleId="cashflow-breakdown-heading"
            testId="cashflow-breakdown"
            zIndex={60}
            eyebrow={planTitle}
            back={canGoBack ? { label: `Back to ${pageName(trail[trail.length - 1])}`, onClick: goBack } : null}
            title={title}
            intro={intro}
            onClose={onClose}
            footer={
                <>
                    {/* The year-by-year figures on every page (owner, 18 Oct 2026): each of these numbers has a yearly line there. */}
                    {onDeepDive && <button type="button" onClick={onDeepDive} className="mr-auto text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="figure-year-by-year">See the year-by-year figures</button>}
                    {canGoBack && <Button type="button" variant="secondary" onClick={goBack}>‹ Back</Button>}
                    <Button type="button" variant="primary" onClick={onClose}>Got it</Button>
                </>
            }
        >

                    {(page === 'pension' || page === 'care') ? (
                        <div data-testid={`figure-${page}-page`}>
                            <DialogSection>The calculation</DialogSection>
                            {(page === 'pension' ? pensionRows : careRows).map((row, i) => {
                                const l = String(row.label ?? '');
                                const isHeading = l.startsWith('---');
                                const indent = /^- /.test(l);
                                return isHeading ? (
                                    <DialogSection key={i} className="mt-6">{l.replace(/-/g, '').trim()}</DialogSection>
                                ) : (
                                    <div key={i} className={`flex items-start justify-between gap-6 border-b border-slate-100 py-2.5 ${indent ? 'pl-5' : ''}`}>
                                        <span className={`text-[14.5px] ${indent ? 'text-slate-500' : 'text-slate-800'}`}>{l.replace(/^- /, '')}</span>
                                        <span className={`shrink-0 text-[14.5px] tabular-nums ${indent ? 'font-medium text-slate-600' : 'font-semibold text-slate-900'}`}>{String(row.value ?? '')}</span>
                                    </div>
                                );
                            })}
                            {page === 'pension' && baseline && (
                                <p className="mt-4 text-[13px] leading-relaxed text-slate-600">Paying daily, {enteringName}&apos;s pension would be {money(baseline.pension)} a year{pensionDelta != null && Math.round(pensionDelta) !== 0 ? <> — this plan {pensionDelta > 0 ? 'lifts' : 'lowers'} it by <strong>{money(pensionDelta)}</strong></> : null}.</p>
                            )}
                            {page === 'care' && deposit.dapAnnual > 0 && (
                                <p className="mt-4 text-[13px] leading-relaxed text-slate-600">{money(deposit.dapAnnual)} of this is the daily room fee on the {money(deposit.unpaid)} of the room not paid up front; the rest is charged whatever is decided about the room.</p>
                            )}
                            {!trail.includes('cashflow') && (
                                <p className="mt-3 text-[13px]"><button type="button" onClick={() => setPage('cashflow')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy">See the whole year — where the {money(net)} {short ? 'shortfall' : 'left over'} comes from ›</button></p>
                            )}
                        </div>
                    ) : !taxPage ? (
                        <>
                            <Section>Money in</Section>
                            <Row label={`${enteringName}'s Age Pension`} value={money(pension)} why onWhy={() => setPage('pension')}
                                sub={pensionDelta != null && Math.round(pensionDelta) > 0 && deposit.paid > 0
                                    ? <>Higher than paying daily because the {money(deposit.paid)} deposit isn&apos;t counted as an asset.</>
                                    : pensionDelta != null && Math.round(pensionDelta) < 0
                                        ? <>Lower than paying daily by {money(pensionDelta)} a year.</>
                                        : <>The same as paying daily.</>} />
                            {abp > 0 && (
                                <Row label="Income from the account-based pensions" value={money(abp)}
                                    sub={<>{abpNames.length ? abpNames.join(' and ') : 'The super pensions'}{abpToDeposit > 0 ? `, after ${money(abpToDeposit)} went to the deposit` : ''}</>} />
                            )}
                            <Row label="Other investment income" value={money(other)} sub={<>Interest, dividends and any rent{rentalExpenses > 0 ? ` — before ${money(rentalExpenses)} of rental expenses, below` : ''}</>} />
                            {rentalExpenses > 0 && <Row label="Less: rental expenses" value={`−${money(rentalExpenses)}`} sub="Rates, insurance and upkeep on the home that is let out" />}
                            {tax < 0 && (
                                <Row label="Tax" value={`+${money(tax)}`} sub={<>A refund — franking credits on the shares come back. <button type="button" onClick={() => setTaxPage(true)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy" data-testid="cashflow-tax-link">See how the tax is worked out ›</button></>} />
                            )}
                            <Section>Money out</Section>
                            <Row label="Care costs" value={`−${money(care)}`} why onWhy={() => setPage('care')}
                                sub={deposit.dapAnnual > 0 ? <>{money(deposit.dapAnnual)} of it is the daily room fee on the {money(deposit.unpaid)} not paid up front.</> : <>The basic daily fee and the means-tested contributions; no daily room fee, because the room is paid in full.</>} />
                            <Row label="Living costs at home" value={`−${money(living)}`} sub={stayingName ? `What ${stayingName} spends, as you entered on step 1` : 'As you entered on step 1'} />
                            {tax > 0 && (
                                <Row label="Tax" value={`−${money(tax)}`} sub={<>Tax on the household&apos;s income. <button type="button" onClick={() => setTaxPage(true)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy" data-testid="cashflow-tax-link">See how the tax is worked out ›</button></>} />
                            )}
                            {tax === 0 && (
                                <Row label="Tax" value="$0" sub={<>Nothing to pay at these income levels. <button type="button" onClick={() => setTaxPage(true)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy" data-testid="cashflow-tax-link">See how it is worked out ›</button></>} />
                            )}
                            <Row bold label={short ? 'Each year, short by' : 'Left over each year'} value={signed(net)} />
                            <p className="mt-4 text-[13px] leading-relaxed text-slate-600">
                                {baseline && (
                                    <>{baseline.netCashflow < 0 ? `Paying daily, it is short by ${money(baseline.netCashflow)}` : `Paying daily, it has ${money(baseline.netCashflow)} to spare`}{closes != null && closes > 0 ? `, so this plan closes ${money(closes)} of the gap` : short && baseline.netCashflow < 0 && Math.abs(net) > Math.abs(baseline.netCashflow) ? `, so this plan widens the gap by ${money(Math.abs(net) - Math.abs(baseline.netCashflow))}` : ''}. </>
                                )}
                                A full 12 months with the plan in place, so every plan compares like for like; the first year is shorter and is shown in the year-by-year figures.
                            </p>
                        </>
                    ) : (
                        <div data-testid="cashflow-tax-page">
                            {cols.length === 0 ? (
                                <p className="mt-4 text-[14px] text-slate-600">No tax is payable on these figures.</p>
                            ) : (
                                <div className={`mt-2 grid grid-cols-1 gap-6 ${cols.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                                    {cols.map((c, i) => (
                                        <div key={`${c.name}-${i}`}>
                                            {c.name && <DialogSection>{c.name}</DialogSection>}
                                            {taxLinesFor(c.rows, adviserView).map((t, j) => (
                                                <div key={j} className={`flex items-start justify-between gap-3 ${t.bold ? 'mt-1 border-t-2 border-brand-navy pt-3 pb-1' : 'border-b border-slate-100 py-2.5'} ${t.indent ? 'pl-5' : ''}`}>
                                                    <div className="min-w-0">
                                                        <p className={`text-[14px] ${t.bold ? 'font-bold text-brand-navy' : t.indent ? 'text-slate-500' : 'text-slate-800'}`}>{t.label}</p>
                                                        {t.sub && <p className="mt-0.5 text-[12px] leading-relaxed text-slate-500">{t.sub}</p>}
                                                    </div>
                                                    <p className={`shrink-0 tabular-nums ${t.bold ? 'text-[15px] font-bold text-brand-navy' : t.indent ? 'text-[14px] font-medium text-slate-600' : 'text-[14px] font-semibold text-slate-900'}`}>{t.value}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="mt-4 text-[13px] leading-relaxed text-slate-600">
                                Together, {tax < 0 ? <>a <strong>{money(tax)} refund</strong></> : tax > 0 ? <><strong>{money(tax)}</strong> of tax</> : <>nothing to pay</>} — the &quot;Tax&quot; line in the cashflow. The account-based pensions are tax-free at this age, so they don&apos;t appear.
                                {' '}
                                <button type="button" onClick={() => setAdviserView((v) => !v)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-[3px] hover:decoration-brand-navy">{adviserView ? 'Hide the nil lines' : 'Show every line (adviser view)'}</button>
                            </p>
                        </div>
                    )}
        </DialogShell>
    );
};

export default CashflowBreakdownModal;
